/**
 * Экран второго фактора: 6-значный код из письма.
 *
 * Поведение полей сделано так, как этого ждут от OTP-ввода: цифра — переход к
 * следующему квадрату, Backspace на пустом — возврат к предыдущему, вставка всего
 * кода целиком раскладывается по полям, а заполнение шестого поля само отправляет
 * форму (кнопка остаётся фолбэком для тех, кто дозаполнил поле в середине).
 *
 * ── Оформление (переделано 27.08) ────────────────────────────────────────
 * Замечание Ирины: экран остался на старом языке, пока соседние шаги визарда
 * регистрации уже перешли на дизайн-систему. Разбор и рецепты — в app.css,
 * раздел «ВВОД КОДА ИЗ ПИСЬМА». Коротко: квадраты — тот же `.rla-control`, что
 * и остальные поля формы, подписи — `.rla-dialog-title`/`.rla-dialog-text`/
 * `.rla-hint`, ошибка — `--rla-alarm`.
 *
 * Кнопка «Подтвердить» — `.rla-btn rla-btn-cta rla-btn-full`, тот же рецепт,
 * что у остальных экранов AuthModal (Reset.tsx, ResetToken.tsx) и что этому
 * же submit-у уже дал `fix: legacy CTA button anti-pattern sweep` (21.08) —
 * не `.rla-cta` визарда регистрации, это другая, самостоятельная кнопка.
 *
 * DEV-режим убран отдельно, полностью (fix(auth): убрать DEV-режим бейдж,
 * 28.08) — этот экран его больше не показывает вовсе, не перекрашивает.
 *
 * Движение (apple-design): цифра «садится» в квадрат пружиной без перелёта
 * (bounce 0.3, 0.32 s — отклик на уже совершённое действие, поэтому лёгкий
 * перелёт уместен), подмена таймера на кнопку повтора — кросс-фейд вместо
 * подмены кадром. Тряска на ошибке была и осталась. Всё уважает
 * prefers-reduced-motion.
 *
 * Логика НЕ тронута: раскладка цифр по полям, автопереход, вставка кода,
 * автоотправка на шестой цифре, отсчёт до повторной отправки, вызовы
 * onSubmit/onResend — ровно те же, что и были.
 */
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {ChangeEvent, ClipboardEvent, FormEvent, KeyboardEvent} from "react";
import {ArrowLeft, Mail} from "lucide-react";
import {AnimatePresence, motion, useAnimationControls, useReducedMotion} from "motion/react";

const CODE_LENGTH = 6;

interface TwoFactorFormProps {
    /**
     * Идентификатор текущего кода. Используется как признак «выпущен новый код»:
     * при повторной отправке сервер возвращает тот же resendAfterSeconds (60),
     * поэтому по одному только числу перезапуск таймера не отследить.
     */
    challengeId: string;
    /** Замаскированный адрес: «куда ушло письмо», без выдачи самого адреса. */
    emailHint: string;
    /** Сколько секунд ждать до повторной отправки. */
    resendAfterSeconds: number;
    error: string | null;
    /**
     * Растёт на каждой неудаче. Нужен потому, что две одинаковые ошибки подряд
     * («Неверный код» дважды) не меняют строку `error`, и без счётчика второй
     * промах не очистил бы поля и не тряхнул бы их.
     */
    errorKey: number;
    submitting: boolean;
    resending: boolean;
    /** false — письмо с кодом не ушло (#1271): не пишем «мы отправили». */
    emailDelivered?: boolean;
    /** Подпись кнопки возврата — по умолчанию «Вернуться ко входу» (экран входа); регистрация передаёт свою. */
    backLabel?: string;
    onSubmit: (code: string) => void;
    onResend: () => void;
    onBackToLogin: () => void;
    onClearError: () => void;
}

function formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function onlyDigits(value: string): string {
    return value.replace(/\D/g, "");
}

interface OtpCellProps {
    value: string;
    index: number;
    invalid: boolean;
    disabled: boolean;
    setRef: (element: HTMLInputElement | null) => void;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
}

/**
 * Один квадрат кода. Отдельный компонент нужен ровно ради одной вещи —
 * собственного отклика на попадание цифры: у каждой ячейки свои controls,
 * иначе при вставке кода целиком «сели» бы все шесть одной анимацией.
 *
 * Пружина, а не keyframes: при быстром наборе (или сразу после вставки)
 * следующий запуск перехватывает текущее значение вместо скачка — то самое
 * «анимация начинается от того, что на экране» из apple-design.
 */
function OtpCell({value, index, invalid, disabled, setRef, onChange, onKeyDown, onPaste}: OtpCellProps) {
    const controls = useAnimationControls();
    const reduceMotion = useReducedMotion();
    const wasFilled = useRef(value !== "");

    useEffect(() => {
        const filled = value !== "";
        // Отклик только на «появилась цифра»: на стирание и на перерисовку с
        // тем же значением квадрат не дёргается.
        if (filled && !wasFilled.current && !reduceMotion) {
            controls.set({scale: 1.08});
            void controls.start({scale: 1, transition: {type: "spring", bounce: 0.3, duration: 0.32}});
        }
        wasFilled.current = filled;
    }, [value, controls, reduceMotion]);

    return (
        <motion.input
            animate={controls}
            ref={setRef}
            type="text"
            inputMode="numeric"
            // Код из письма/SMS браузер подставляет сам только в первое поле.
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={CODE_LENGTH}
            disabled={disabled}
            aria-label={`Цифра ${index + 1} из ${CODE_LENGTH}`}
            aria-invalid={invalid}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={(event) => event.target.select()}
            className="rla-control rla-otp-cell"
            data-filled={value ? "" : undefined}
            data-invalid={invalid ? "" : undefined}
        />
    );
}

export function TwoFactorForm(
    {
        challengeId,
        emailHint,
        resendAfterSeconds,
        error,
        errorKey,
        submitting,
        resending,
        emailDelivered = true,
        backLabel = "Вернуться ко входу",
        onSubmit,
        onResend,
        onBackToLogin,
        onClearError,
    }: TwoFactorFormProps) {
    const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(""));
    const [secondsLeft, setSecondsLeft] = useState(resendAfterSeconds);
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const reduceMotion = useReducedMotion();
    const shake = useAnimationControls();

    const code = useMemo(() => digits.join(""), [digits]);
    const complete = code.length === CODE_LENGTH;

    const focusAt = useCallback((index: number) => {
        const target = inputsRef.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
        target?.focus();
        target?.select();
    }, []);

    // Новый код — новый отсчёт и чистые поля: старые цифры уже не подойдут.
    useEffect(() => {
        setSecondsLeft(resendAfterSeconds);
        setDigits(Array(CODE_LENGTH).fill(""));
        focusAt(0);
    }, [challengeId, resendAfterSeconds, focusAt]);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = window.setTimeout(() => setSecondsLeft((prev) => Math.max(0, prev - 1)), 1000);
        return () => window.clearTimeout(timer);
    }, [secondsLeft]);

    // Неверный код: очищаем поля и возвращаем курсор в начало — иначе пользователь
    // правит вслепую поверх старых цифр.
    useEffect(() => {
        if (!error) return;
        setDigits(Array(CODE_LENGTH).fill(""));
        focusAt(0);
        if (!reduceMotion) {
            // Тряска — одноразовый акцент на ошибке; запускаем императивно, чтобы
            // она срабатывала и на повторной ошибке с тем же текстом.
            void shake.start({x: [0, -8, 7, -5, 3, 0], transition: {duration: 0.36, ease: "easeOut"}});
        }
    }, [errorKey, error, focusAt, reduceMotion, shake]);

    const submitCode = useCallback(
        (value: string) => {
            if (value.length !== CODE_LENGTH || submitting) return;
            onSubmit(value);
        },
        [onSubmit, submitting],
    );

    const applyDigits = useCallback(
        (next: string[], caretIndex: number, autoSubmit: boolean) => {
            setDigits(next);
            focusAt(caretIndex);
            const joined = next.join("");
            if (autoSubmit && joined.length === CODE_LENGTH) {
                submitCode(joined);
            }
        },
        [focusAt, submitCode],
    );

    const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const typed = onlyDigits(event.target.value);
        if (!typed) {
            const next = [...digits];
            next[index] = "";
            setDigits(next);
            return;
        }
        if (error) onClearError();

        // Мобильные клавиатуры и автозаполнение из SMS/почты кладут в одно поле
        // сразу несколько цифр — раскладываем их по остальным полям.
        const next = [...digits];
        let cursor = index;
        for (const char of typed) {
            if (cursor >= CODE_LENGTH) break;
            next[cursor] = char;
            cursor += 1;
        }
        applyDigits(next, cursor, true);
    };

    const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace") {
            event.preventDefault();
            const next = [...digits];
            if (next[index]) {
                next[index] = "";
                applyDigits(next, index, false);
                return;
            }
            next[Math.max(0, index - 1)] = "";
            applyDigits(next, index - 1, false);
            return;
        }
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            focusAt(index - 1);
            return;
        }
        if (event.key === "ArrowRight") {
            event.preventDefault();
            focusAt(index + 1);
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        const pasted = onlyDigits(event.clipboardData.getData("text")).slice(0, CODE_LENGTH);
        if (!pasted) return;
        event.preventDefault();
        if (error) onClearError();
        const next = Array(CODE_LENGTH).fill("") as string[];
        for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
        applyDigits(next, pasted.length, true);
    };

    const handleFormSubmit = (event: FormEvent) => {
        event.preventDefault();
        submitCode(code);
    };

    const canResend = secondsLeft <= 0 && !resending && !submitting;

    // Появление и уход строк подвала — только прозрачностью: под reduce-motion
    // остаётся то же самое, но мгновенно.
    const fade = reduceMotion
        ? {initial: {opacity: 1}, animate: {opacity: 1}, exit: {opacity: 1}, transition: {duration: 0}}
        : {
            initial: {opacity: 0},
            animate: {opacity: 1},
            exit: {opacity: 0},
            transition: {duration: 0.18, ease: [0.23, 1, 0.32, 1] as const},
        };

    return (
        <form onSubmit={handleFormSubmit} noValidate className="rla-otp-form">
            <div className="rla-otp-head">
                <span className="rla-mark" aria-hidden="true">
                    <Mail className="size-6"/>
                </span>
                <h3 className="rla-dialog-title">Введите код из письма</h3>
                <p className="rla-dialog-text">
                    {emailDelivered ? "Мы отправили 6-значный код на" : "Код предназначен для"}{" "}
                    <span className="font-semibold text-[var(--rla-ink)]">{emailHint}</span>.
                    <br/>
                    {emailDelivered
                        ? "Проверьте папку «Спам», если письма нет."
                        : "Письмо пока не отправлено — повторите отправку чуть позже."}
                </p>
            </div>

            <motion.div animate={shake} className="rla-otp">
                {digits.map((digit, index) => (
                    <OtpCell
                        key={index}
                        index={index}
                        value={digit}
                        invalid={Boolean(error)}
                        disabled={submitting}
                        setRef={(element) => {
                            inputsRef.current[index] = element;
                        }}
                        onChange={handleChange(index)}
                        onKeyDown={handleKeyDown(index)}
                        onPaste={handlePaste}
                    />
                ))}
            </motion.div>

            <AnimatePresence initial={false}>
                {error && (
                    <motion.p
                        key="otp-error"
                        role="alert"
                        className="rla-otp-err"
                        initial={fade.initial}
                        animate={fade.animate}
                        exit={fade.exit}
                        transition={fade.transition}
                    >
                        {error}
                    </motion.p>
                )}
            </AnimatePresence>

            <button
                type="submit"
                disabled={!complete || submitting}
                className="rla-btn rla-btn-cta rla-btn-full"
            >
                {submitting ? "Проверяем..." : "Подтвердить"}
            </button>

            <div className="rla-otp-foot">
                <AnimatePresence initial={false} mode="wait">
                    {canResend ? (
                        <motion.button
                            key="otp-resend"
                            type="button"
                            onClick={onResend}
                            className="rla-skip-btn rla-otp-again"
                            initial={fade.initial}
                            animate={fade.animate}
                            exit={fade.exit}
                            transition={fade.transition}
                        >
                            Отправить код повторно
                        </motion.button>
                    ) : (
                        <motion.p
                            key="otp-wait"
                            className="rla-hint"
                            initial={fade.initial}
                            animate={fade.animate}
                            exit={fade.exit}
                            transition={fade.transition}
                        >
                            {resending ? (
                                "Отправляем новый код..."
                            ) : (
                                <>
                                    Отправить код повторно через{" "}
                                    <span className="rla-otp-timer">{formatCountdown(secondsLeft)}</span>
                                </>
                            )}
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>

            <div className="text-center">
                <button type="button" onClick={onBackToLogin} className="rla-skip-btn rla-otp-back">
                    <ArrowLeft className="size-4"/>
                    {backLabel}
                </button>
            </div>
        </form>
    );
}
